import { mkLeadsUrl } from '@/utils/config';
import { AUDIT_STATUS, IMAGE_TYPE, PUBLISHED, SITE_TYPE, VIEW_STATUS } from './constants';

export const PRODUCT_CASE_FIELD_LIMITS = {
  NAME: 200,
  KEYWORD: 30,
  KEYWORD_COUNT: 3,
  PRICE: 50,
  MIN_ORDER_AMOUNT: 9,
  PRODUCE_ABILITY: 50,
  DESCRIPTION: 5000
};

function createClientId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeKeywords(keywords) {
  if (Array.isArray(keywords)) {
    return keywords.map(normalizeKeyword).filter(item => item.keyword);
  }
  if (!keywords) return [];
  return String(keywords).split(/[,，、;；]/).map(item => normalizeKeyword(item)).filter(item => item.keyword);
}


function normalizeFaqItem(item) {
  if (!item || typeof item !== 'object') {
    return { question: '', answer: '' };
  }
  const normalized = {
    question: String(item.question || item.q || '').trim(),
    answer: String(item.answer || item.a || '').trim()
  };
  if (item.focus_angle) {
    normalized.focus_angle = String(item.focus_angle).trim();
  }
  return normalized;
}

export function parseFaqJson(faqJson) {
  if (Array.isArray(faqJson)) return normalizeFaqList(faqJson);
  const value = String(faqJson || '').trim();
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? normalizeFaqList(parsed) : [];
  } catch (error) {
    return [];
  }
}

export function normalizeFaqList(faqList, options = {}) {
  const list = (Array.isArray(faqList) ? faqList : []).map(normalizeFaqItem);
  return options.keepEmpty ? list : list.filter(item => item.question || item.answer);
}

export function stringifyFaqList(faqList, options = {}) {
  const list = normalizeFaqList(faqList, options);
  return list.length ? JSON.stringify(list) : '';
}

export function normalizeKeyword(keyword) {
  if (!keyword) return { id: null, keyword: '' };
  if (typeof keyword === 'object') {
    return {
      id: keyword.id || null,
      keyword: String(keyword.keyword || keyword.name || keyword.value || '').trim()
    };
  }
  return {
    id: null,
    keyword: String(keyword).trim()
  };
}

export function getKeywordText(keyword) {
  return normalizeKeyword(keyword).keyword;
}

export function extractRspData(res) {
  if (!res) return {};
  return res.data || res.rspPageData || res.rc || {};
}

export function isRequestSuccess(res) {
  if (!res || !res.rspResult) return true;
  return res.rspResult.bizCode === 200 || res.rspResult.resultCode === 'SUCCESS';
}

export function createEmptyPart(siteType = SITE_TYPE.CN) {
  return {
    clientPartId: createClientId('part'),
    id: null,
    siteType,
    sourceProductId: null,
    productGroupId: null,
    cnName: '',
    enName: '',
    cnContent: '',
    enContent: '',
    faqJson: '',
    faqList: [],
    faqStatus: 'success',
    keywords: [],
    mainCategory: null,
    mainCategoryName: '',
    subCategory: null,
    subCategoryName: '',
    material: null,
    materialName: '',
    minPrice: null,
    maxPrice: null,
    priceRange: '',
    currency: null,
    unit: null,
    minOrderAmount: null,
    minOrderUnit: null,
    produceAbility: null,
    produceAbilityType: null,
    productPictureList: [],
    defaultPhoto: '',
    published: PUBLISHED.DRAFT,
    status: AUDIT_STATUS.REVIEWING,
    parseStatus: 'success',
    warnings: []
  };
}

export function createImageItem(uploadRsp, sort = 1) {
  const hash = uploadRsp.hashCode || uploadRsp.selectedHashCode || '';
  return {
    id: uploadRsp.id || null,
    clientImageId: uploadRsp.clientImageId || createClientId('img'),
    originalHashCode: uploadRsp.originalHashCode || hash,
    processedHashCode: uploadRsp.processedHashCode || null,
    selectedHashCode: uploadRsp.selectedHashCode || hash,
    selectedImageType: uploadRsp.selectedImageType || IMAGE_TYPE.ORIGINAL,
    processStatus: uploadRsp.processStatus || 'skipped',
    processMessage: uploadRsp.processMessage || '',
    sort: uploadRsp.sort || sort,
    isMain: !!uploadRsp.isMain,
    originalName: uploadRsp.originalName || uploadRsp.name || '',
    url: uploadRsp.url || '',
    previewUrl: uploadRsp.previewUrl || '',
    originalPreviewUrl: uploadRsp.originalPreviewUrl || uploadRsp.previewUrl || '',
    processedPreviewUrl: uploadRsp.processedPreviewUrl || '',
    isCurrentUpload: uploadRsp.isCurrentUpload !== undefined ? uploadRsp.isCurrentUpload : !uploadRsp.id
  };
}

function buildPriceRange(part) {
  if (part.priceRange) return part.priceRange;
  if (part.minPrice && part.maxPrice) return `${part.minPrice}~${part.maxPrice}`;
  return part.minPrice || part.maxPrice || '';
}

function parsePriceRange(priceRange) {
  const value = String(priceRange || '').trim();
  if (!value) {
    return { minPrice: '', maxPrice: '' };
  }
  const normalized = value.replace(/[￥¥$€£]/g, '').trim();
  const parts = normalized.split(/\s*(?:~|～|-|—|至|到)\s*/).filter(Boolean);
  return {
    minPrice: parts[0] || normalized,
    maxPrice: parts[1] || ''
  };
}

function formatPriceValue(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function parsePriceNumber(value) {
  const normalized = formatPriceValue(value);
  if (!normalized) return null;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return NaN;
  return Number(normalized);
}

function hasInvalidPriceScale(value) {
  const normalized = formatPriceValue(value);
  return /^\d+(\.\d+)?$/.test(normalized) && !/^\d+(\.\d{1,2})?$/.test(normalized);
}

function textLength(value) {
  return String(value === undefined || value === null ? '' : value).trim().length;
}


function decodeHtmlText(value) {
  if (!/&(?:lt|gt|amp|quot|#39);/i.test(value)) return value;
  if (typeof document === 'undefined') {
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

export function normalizeRichTextContent(value) {
  const content = decodeHtmlText(String(value || '').trim());
  const bodyMatch = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[1].trim() : content;
}

function isSavableImage(image) {
  return hasText(image && image.originalHashCode) && hasText(image && image.selectedHashCode);
}

function normalizeImagesForSave(images, defaultPhoto) {
  return (images || [])
    .filter(isSavableImage)
    .map((image, index) => ({
      ...image,
      sort: index + 1,
      isMain: isMainImage(image, defaultPhoto)
    }));
}

export function preparePartForSave(part) {
  const productPictureList = normalizeImagesForSave(part.productPictureList, part.defaultPhoto);
  const mainImage = productPictureList.find(image => image.isMain);
  const minPrice = formatPriceValue(part.minPrice);
  const maxPrice = formatPriceValue(part.maxPrice);
  const hasPrice = Boolean(minPrice || maxPrice);
  const hasMinOrderAmount = Boolean(formatPriceValue(part.minOrderAmount));
  const produceAbility = formatPriceValue(part.produceAbility);
  return {
    ...part,
    productPictureList,
    defaultPhoto: mainImage ? mainImage.selectedHashCode : part.defaultPhoto,
    cnContent: normalizeRichTextContent(part.cnContent),
    enContent: normalizeRichTextContent(part.enContent),
    faqJson: stringifyFaqList(part.faqList || parseFaqJson(part.faqJson)),
    minPrice,
    maxPrice,
    currency: hasPrice ? part.currency : null,
    minOrderUnit: hasMinOrderAmount ? part.minOrderUnit : null,
    produceAbility,
    produceAbilityType: produceAbility ? part.produceAbilityType : null
  };
}

export function normalizeImageItem(item, index = 0) {
  const normalized = createImageItem({ ...item }, index + 1);
  if (item.hashCode && !item.originalHashCode) normalized.originalHashCode = item.hashCode;
  if (!normalized.selectedHashCode) {
    normalized.selectedHashCode = normalized.processedHashCode || normalized.originalHashCode;
    normalized.selectedImageType = normalized.processedHashCode ? IMAGE_TYPE.PROCESSED : IMAGE_TYPE.ORIGINAL;
  }
  return normalized;
}

export function normalizePart(part, siteType = SITE_TYPE.CN) {
  const normalizedSiteType = Number(part.siteType || siteType);
  const parsedPrice = parsePriceRange(part.priceRange);
  const normalized = {
    ...createEmptyPart(siteType),
    ...part,
    siteType: normalizedSiteType,
    cnName: part.cnName || (normalizedSiteType === SITE_TYPE.CN ? part.name : '') || '',
    enName: part.enName || (normalizedSiteType === SITE_TYPE.INTL ? part.name : '') || '',
    cnContent: normalizeRichTextContent(part.cnContent || (normalizedSiteType === SITE_TYPE.CN ? part.contentSummary : '') || ''),
    enContent: normalizeRichTextContent(part.enContent || (normalizedSiteType === SITE_TYPE.INTL ? part.contentSummary : '') || ''),
    faqJson: part.faqJson || stringifyFaqList(part.faqList),
    faqList: normalizeFaqList((part.faqList && part.faqList.length) ? part.faqList : parseFaqJson(part.faqJson)),
    faqStatus: part.faqStatus || 'success',
    keywords: normalizeKeywords(part.keywords || part.keyword || part.keywordsText),
    minPrice: formatPriceValue(part.minPrice) || parsedPrice.minPrice,
    maxPrice: formatPriceValue(part.maxPrice) || parsedPrice.maxPrice,
    priceRange: buildPriceRange(part)
  };
  normalized.productPictureList = (part.productPictureList || part.images || []).map(normalizeImageItem);
  if (!normalized.defaultPhoto && normalized.productPictureList.length) {
    const mainImage = normalized.productPictureList.find(item => item.isMain);
    normalized.defaultPhoto = (mainImage || normalized.productPictureList[0]).selectedHashCode;
  }
  return syncMainFlag(normalized);
}

export function syncMainFlag(part) {
  const defaultPhoto = part.defaultPhoto;
  part.productPictureList = (part.productPictureList || []).map((item, index) => ({
    ...item,
    sort: item.sort || index + 1,
    isMain: isMainImage(item, defaultPhoto)
  }));
  return part;
}

export function isMainImage(image, defaultPhoto) {
  if (!image) return false;
  if (!defaultPhoto) return !!image.isMain;
  return [image.selectedHashCode, image.processedHashCode, image.originalHashCode, image.hashCode].includes(defaultPhoto);
}

export function sortPartImages(images, defaultPhoto) {
  return [...(images || [])]
    .map((image, index) => ({ image, index }))
    .sort((a, b) => {
      const aMain = isMainImage(a.image, defaultPhoto);
      const bMain = isMainImage(b.image, defaultPhoto);
      if (aMain !== bMain) return aMain ? -1 : 1;
      const aSort = Number(a.image.sort || 0);
      const bSort = Number(b.image.sort || 0);
      if (aSort && bSort && aSort !== bSort) return aSort - bSort;
      return a.index - b.index;
    })
    .map(item => item.image);
}

export function preferProcessedImages(part) {
  (part.productPictureList || []).forEach(image => {
    if (image.processedHashCode && image.processStatus !== 'failed') {
      image.selectedImageType = IMAGE_TYPE.PROCESSED;
      image.selectedHashCode = image.processedHashCode;
    }
  });
  const mainImage = (part.productPictureList || []).find(item => item.isMain) || (part.productPictureList || [])[0];
  if (mainImage) part.defaultPhoto = mainImage.selectedHashCode;
  return syncMainFlag(part);
}

export function setPartMainImage(part, image) {
  part.defaultPhoto = image.selectedHashCode;
  return syncMainFlag(part);
}

export function selectImageVersion(part, image, imageType) {
  if (imageType === IMAGE_TYPE.PROCESSED && !image.processedHashCode) return part;
  image.selectedImageType = imageType;
  image.selectedHashCode = imageType === IMAGE_TYPE.PROCESSED ? image.processedHashCode : image.originalHashCode;
  if (image.isMain) part.defaultPhoto = image.selectedHashCode;
  return syncMainFlag(part);
}

export function validatePartImages(part) {
  const list = normalizeImagesForSave(part.productPictureList, part.defaultPhoto);
  if (!list.length) return '请至少上传 1 张零件图片';
  if (list.length > 5) return '单个零件最多上传 5 张图片';
  if (!part.defaultPhoto) return '请设置主图';
  if (!list.some(item => item.selectedHashCode === part.defaultPhoto)) return '主图必须来自当前零件图片';
  return '';
}

export function validatePartNumericField(field, value) {
  const text = formatPriceValue(value);
  if (!text) return '';
  if (field === 'minPrice' || field === 'maxPrice') {
    const price = parsePriceNumber(text);
    if (!Number.isFinite(price) || price <= 0) return '价格请输入大于0的数字';
    if (hasInvalidPriceScale(text)) return '价格最多只能输入2位小数';
    return '';
  }
  const label = field === 'minOrderAmount' ? '最小起订量' : '产能';
  return /^[1-9]\d*$/.test(text) ? '' : `${label}请输入正整数`;
}

export function validatePartPrice(part) {
  for (const field of ['minPrice', 'maxPrice', 'minOrderAmount', 'produceAbility']) {
    const error = validatePartNumericField(field, part[field]);
    if (error) return error;
  }
  const minPriceText = formatPriceValue(part.minPrice);
  const maxPriceText = formatPriceValue(part.maxPrice);
  const hasPrice = Boolean(minPriceText || maxPriceText);
  const hasCurrency = part.currency !== undefined && part.currency !== null && String(part.currency).trim() !== '';
  const minOrderAmountText = formatPriceValue(part.minOrderAmount);
  const hasMinOrderAmount = Boolean(minOrderAmountText);
  const hasMinOrderUnit = part.minOrderUnit !== undefined && part.minOrderUnit !== null && String(part.minOrderUnit).trim() !== '';
  const produceAbilityText = formatPriceValue(part.produceAbility);
  const hasProduceAbility = Boolean(produceAbilityText);
  const hasProduceAbilityType = part.produceAbilityType !== undefined && part.produceAbilityType !== null
    && String(part.produceAbilityType).trim() !== '';
  if (hasPrice && !hasCurrency) return '请选择价格币种';
  if (hasMinOrderAmount && !hasMinOrderUnit) return '请选择最小起订量单位';
  if (hasProduceAbility && !hasProduceAbilityType) return '请选择产能单位';
  if (!minPriceText || !maxPriceText) return '';
  const minPrice = parsePriceNumber(minPriceText);
  const maxPrice = parsePriceNumber(maxPriceText);
  if (minPrice > maxPrice) return '最低价不能超过最高价';
  return '';
}

export function validatePartFieldLengths(part, siteType) {
  const isInternational = Number(siteType) === SITE_TYPE.INTL;
  const name = isInternational ? part.enName : part.cnName;
  const content = isInternational ? part.enContent : part.cnContent;
  if (textLength(name) > PRODUCT_CASE_FIELD_LIMITS.NAME) return '零件名称最多200个字符';
  if ((part.keywords || []).length > PRODUCT_CASE_FIELD_LIMITS.KEYWORD_COUNT) return '关键词最多添加3个';
  const overLengthKeyword = (part.keywords || []).map(getKeywordText).find(keyword => textLength(keyword) > PRODUCT_CASE_FIELD_LIMITS.KEYWORD);
  if (overLengthKeyword) return '单个关键词最多30个字符';
  if (textLength(part.minPrice) > PRODUCT_CASE_FIELD_LIMITS.PRICE) return '最低价最多50个字符';
  if (textLength(part.maxPrice) > PRODUCT_CASE_FIELD_LIMITS.PRICE) return '最高价最多50个字符';
  if (textLength(part.minOrderAmount) > PRODUCT_CASE_FIELD_LIMITS.MIN_ORDER_AMOUNT) return '最小起订量最多9位数字';
  if (textLength(part.produceAbility) > PRODUCT_CASE_FIELD_LIMITS.PRODUCE_ABILITY) return '产能最多50个字符';
  if (textLength(content) > PRODUCT_CASE_FIELD_LIMITS.DESCRIPTION) return '零件描述最多5000个字符';
  return '';
}

function hasText(value) {
  return String(value || '').trim().length > 0;
}

function hasRichText(value) {
  const text = normalizeRichTextContent(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length > 0;
}

export function validatePartRequired(part, siteType) {
  const isInternational = Number(siteType) === SITE_TYPE.INTL;
  const name = isInternational ? part.enName : part.cnName;
  const content = isInternational ? part.enContent : part.cnContent;
  if (!hasText(name)) return '请填写零件名称';
  if (!part.mainCategory && !hasText(part.mainCategoryName)) return '请选择主工艺';
  if (!part.subCategory && !hasText(part.subCategoryName)) return '请选择子工艺';
  if (!part.material && !hasText(part.materialName)) return '请选择材料';
  if (!hasRichText(content)) return '请填写零件描述';
  return validatePartImages(part);
}

export function validatePartDraftRequired(part) {
  if (!part.mainCategory) return '请选择主工艺';
  return '';
}

export function getDisplayName(part, siteType) {
  if (!part) return '-';
  return Number(siteType || part.siteType) === SITE_TYPE.INTL
    ? (part.enName || '-')
    : (part.cnName || part.enName || '-');
}

export function getDisplayContent(part, siteType) {
  if (!part) return '';
  return Number(siteType || part.siteType) === SITE_TYPE.INTL
    ? (part.enContent || '')
    : (part.cnContent || part.enContent || '');
}

export function getStatusText(row) {
  if (!row) return '-';
  if (Number(row.published) === PUBLISHED.DRAFT) return '草稿';
  if (Number(row.published) === PUBLISHED.OFFLINE) return '已下架';
  if (Number(row.published) === PUBLISHED.SUBMITTED && Number(row.status) === AUDIT_STATUS.REVIEWING) return '审核中';
  if (Number(row.published) === PUBLISHED.SUBMITTED && Number(row.status) === AUDIT_STATUS.APPROVED) return '已上架';
  return '-';
}

export function getStatusClass(row) {
  const text = getStatusText(row);
  return {
    草稿: 'draft',
    已下架: 'offline',
    审核中: 'reviewing',
    已上架: 'published'
  }[text] || 'unknown';
}

export function getViewStatusQuery(viewStatus) {
  if (viewStatus === VIEW_STATUS.PUBLISHED) return { published: 1, status: 1 };
  if (viewStatus === VIEW_STATUS.REVIEWING) return { published: 1, status: 0 };
  if (viewStatus === VIEW_STATUS.OFFLINE) return { published: 0 };
  if (viewStatus === VIEW_STATUS.DRAFT) return { published: 2 };
  return {};
}

export function canSaveDraftForParts(parts) {
  return !(parts || []).some(part => Number(part.published) === 1);
}

export function buildAttachmentPreviewUrl(hashCode, isCurrentUpload = false) {
  if (!hashCode) return '';
  if (isCurrentUpload) {
    return `${mkLeadsUrl}/mk-leads/common/attach/preview-temp-file/${hashCode}`;
  }
  return `${mkLeadsUrl}/mk-leads/common/attach/preview-file/${encodeURIComponent(hashCode)}`;
}

export function buildFormalAttachmentPreviewUrl(hashCode) {
  return buildAttachmentPreviewUrl(hashCode, false);
}

export function getImagePreview(image) {
  if (!image) return '';
  if (image.selectedImageType === IMAGE_TYPE.PROCESSED && image.processedHashCode) {
    return getProcessedPreview(image);
  }
  if (image.selectedImageType === IMAGE_TYPE.ORIGINAL) {
    return getOriginalPreview(image);
  }
  return buildAttachmentPreviewUrl(image.selectedHashCode || image.hashCode || image.originalHashCode, image.isCurrentUpload);
}

export function getOriginalPreview(image) {
  if (!image) return '';
  return buildAttachmentPreviewUrl(image.originalHashCode, image.isCurrentUpload);
}

export function getProcessedPreview(image) {
  if (!image || !image.processedHashCode) return '';
  if (image.isCurrentUpload) {
    return buildAttachmentPreviewUrl(image.processedHashCode, true);
  }
  return buildFormalAttachmentPreviewUrl(image.processedHashCode);
}
